import { SESSION_STORAGE_KEYS } from '@/lib/consts/sessionStorage';
import type { HandoffTokenIdentity } from '@/lib/utils/handoffTokenIdentity';

/**
 * A pre-auth landing that fails hard-navigates to /failed, which tears down the
 * JS context along with the RUM identity attached on /login — so the page that
 * actually shows the failure reports no player. Parking the identity for the
 * rest of the tab's journey is what makes the failure attributable.
 *
 * sessionStorage, not localStorage: this is scoped to one journey. A stale
 * identity leaking into a later anonymous session would be worse than no
 * identity at all, so it is also bounded by the handoff token's own lifetime.
 */
const PENDING_IDENTITY_TTL_MS = 5 * 60 * 1000;

interface StoredIdentity extends HandoffTokenIdentity {
    storedAt: number;
}

export const setPendingRumIdentity = (identity: HandoffTokenIdentity): void => {
    try {
        const stored: StoredIdentity = { ...identity, storedAt: Date.now() };
        sessionStorage.setItem(SESSION_STORAGE_KEYS.PENDING_RUM_IDENTITY, JSON.stringify(stored));
    } catch {
        // Private mode / quota. The in-memory identity still covers this document.
    }
};

export const getPendingRumIdentity = (): HandoffTokenIdentity | null => {
    try {
        const raw = sessionStorage.getItem(SESSION_STORAGE_KEYS.PENDING_RUM_IDENTITY);
        if (!raw) {
            return null;
        }
        const stored = JSON.parse(raw) as StoredIdentity;
        if (!stored?.playerId || !isFresh(stored.storedAt)) {
            clearPendingRumIdentity();
            return null;
        }
        return {
            playerId: stored.playerId,
            playerName: stored.playerName,
            playerCountry: stored.playerCountry,
        };
    } catch {
        return null;
    }
};

export const clearPendingRumIdentity = (): void => {
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.PENDING_RUM_IDENTITY);
    } catch {
        // nothing to recover from — the TTL below already bounds a stale value
    }
};

const isFresh = (storedAt: number): boolean =>
    typeof storedAt === 'number' && Date.now() - storedAt < PENDING_IDENTITY_TTL_MS;
